import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Warehouse, Save, Loader2, MapPin, ArrowRightLeft, Tag, DollarSign, Calculator, Lock } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import UniversalLookup from './UniversalLookup';

const AddItemToStoreModal = ({ isOpen, onClose, itemnum, onRefresh }) => {
  const { t, i18n } = useTranslation();
  // التحقق من اللغات التي تكتب من اليمين لليسار (عربي وأوردو)
  const isRTL = ['ar', 'ur'].includes(i18n.language);

  const [storeData, setStoreData] = useState({ 
    location: '', 
    siteid: '', 
    orderunit: '', 
    issueunit: '',
    curbal: 0,  
    avgcost: 0   
  });
  
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {} });
  const [loading, setLoading] = useState(false);

  const totalValue = (Number(storeData.curbal) * Number(storeData.avgcost)).toFixed(2);

  useEffect(() => {
    if (isOpen && itemnum) {
      const fetchItemUnits = async () => {
        try {
          const res = await axios.get(`/api/items/${itemnum}`);
          setStoreData(prev => ({
            ...prev,
            orderunit: res.data.orderunit || '',
            issueunit: res.data.issueunit || ''
          }));
        } catch (err) {
          console.error("Error fetching units:", err);
        }
      };
      fetchItemUnits();
    } else {
      setStoreData({ location: '', siteid: '', orderunit: '', issueunit: '', curbal: 0, avgcost: 0 });
    }
  }, [isOpen, itemnum]);

  if (!isOpen) return null;

  const handleAssign = async () => {
    if (!storeData.location || !storeData.siteid) {
      return toast.error(t('add_item_store.err_select_store'));
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/inventory/assign', {
        itemnum: itemnum,
        ...storeData,
        curbal: Number(storeData.curbal),
        avgcost: Number(storeData.avgcost)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // نستخدم {{location}} كما هو متبع في i18next
      toast.success(t('add_item_store.success_init', { location: storeData.location }));
      onRefresh(); 
      onClose();   
    } catch (err) {
      toast.error(err.response?.data?.detail || t('add_item_store.fail_init'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`fixed inset-0 z-[4000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200 uppercase  ${isRTL ? 'font-arabic' : ''}`}>
      <div className="bg-white w-[600px] shadow-2xl rounded-sm overflow-hidden border border-gray-300 animate-in zoom-in-95">
        
        {/* Header */}
        <div className="bg-[#1a3353] text-white p-4 flex justify-between items-center shadow-md border-b border-blue-400/20">
          <div className="flex items-center gap-3">
            <Warehouse size={18} className="text-blue-300" />
            <span className="text-[11px] font-black tracking-widest uppercase italic text-blue-50">
              {t('add_item_store.title')}
            </span>
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors text-white">
            <X size={18}/>
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 bg-gray-50/50 max-h-[80vh] overflow-y-auto custom-scrollbar">
          
          {/* Item Identification */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 bg-white p-3 rounded shadow-sm border-s-4 border-s-blue-600">
             <div className="flex flex-col">
                <label className="text-[8px] font-black text-gray-400 tracking-widest uppercase italic">{t('add_item_store.master_record')}</label>
                <span className="text-[#1a3353] font-black text-sm">{itemnum}</span>
             </div>
             <Tag size={20} className="text-gray-200" />
          </div>

          {/* Location & Locked Site selection */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1 border-b border-gray-300 group focus-within:border-blue-600 transition-all">
                <label className="text-[9px] font-black text-gray-400 tracking-widest uppercase">{t('add_item_store.storeroom_loc')}</label>
                <div className="flex items-center justify-between py-1">
                    <span className={`font-black text-[11px] ${storeData.location ? 'text-blue-900' : 'text-gray-300 italic'}`}>
                        {storeData.location || t('add_item_store.search_store')}
                    </span>
                    <button onClick={() => setLookup({ isOpen: true, title: t('add_item_store.storeroom_loc'), type: 'storerooms', field: 'location' })}>
                        <Search size={14} className="text-blue-600 hover:scale-110 transition-transform" />
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-100/50 p-1 rounded-t opacity-80 cursor-not-allowed">
                <label className="text-[9px] font-black text-gray-400 tracking-widest uppercase flex items-center gap-2">
                   <Lock size={10}/> {t('add_item_store.site_id_locked')}
                </label>
                <div className="flex items-center justify-between py-1">
                    <span className="font-black text-gray-600 text-[11px]">
                        {storeData.siteid || t('add_item_store.auto')}
                    </span>
                </div>
            </div>
          </div>

          {/* Opening Balance Valuation */}
          <div className="bg-white p-5 border rounded-sm shadow-sm space-y-5 border-t-4 border-t-green-600">
             <div className="flex items-center gap-2 text-green-700 font-black mb-2">
                <DollarSign size={14}/> 
                <span className="text-[9px] tracking-widest uppercase">{t('add_item_store.opening_valuation')}</span>
             </div>

             <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col border-b border-gray-200 focus-within:border-green-600 transition-all">
                    <label className="text-[8px] font-black text-gray-400 uppercase tracking-tighter">{t('add_item_store.initial_qty')}</label>
                    <input 
                        type="number"
                        className={`font-black text-blue-900 outline-none py-2 bg-transparent text-lg font-mono placeholder:text-gray-200 ${isRTL ? 'text-right' : 'text-left'}`}
                        value={storeData.curbal}
                        onChange={(e) => setStoreData({...storeData, curbal: e.target.value})}
                    />
                </div>
                <div className={`flex flex-col border-b border-gray-200 focus-within:border-green-600 transition-all ${isRTL ? 'text-left' : 'text-right'}`}>
                    <label className="text-[8px] font-black text-gray-400 uppercase tracking-tighter">{t('add_item_store.unit_cost')}</label>
                    <input 
                        type="number"
                        className={`font-black text-blue-900 outline-none py-2 bg-transparent text-lg font-mono placeholder:text-gray-200 ${isRTL ? 'text-left' : 'text-right'}`}
                        value={storeData.avgcost}
                        onChange={(e) => setStoreData({...storeData, avgcost: e.target.value})}
                    />
                </div>
             </div>

             <div className="bg-green-50 p-3 rounded flex justify-between items-center border border-green-100">
                <div className="flex items-center gap-2">
                    <Calculator size={16} className="text-green-600"/>
                    <span className="text-[9px] font-black text-green-800 uppercase tracking-widest italic">{t('add_item_store.inv_total')}</span>
                </div>
                <span className={`text-xl font-black text-green-700 font-mono tracking-tighter flex gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {totalValue} <span className="text-[10px] font-sans mt-1">{t('add_item_store.currency')}</span>
                </span>
             </div>
          </div>

          {/* Tracking Units */}
          <div className="bg-white p-4 border rounded-sm shadow-sm space-y-4 border-t-4 border-t-[#1a3353]">
            <div className="flex items-center gap-2 mb-2 text-[#1a3353] font-black">
                <ArrowRightLeft size={14} className="text-blue-600"/>
                <span className="text-[9px] tracking-widest uppercase">{t('add_item_store.tracking_units')}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
                <UnitLookupField label={t('add_item_store.order_unit')} value={storeData.orderunit} onSearch={() => setLookup({ isOpen: true, title: 'UOM', type: 'DOMAIN', domainId: 'UOM', field: 'orderunit' })} />
                <UnitLookupField label={t('add_item_store.issue_unit')} value={storeData.issueunit} onSearch={() => setLookup({ isOpen: true, title: 'UOM', type: 'DOMAIN', domainId: 'UOM', field: 'issueunit' })} />
            </div>
          </div>

          <div className="bg-blue-50/50 p-4 border-s-4 border-blue-900 flex gap-3 items-start shadow-inner">
             <MapPin size={16} className="text-blue-600 shrink-0 mt-0.5"/>
             <p className="text-[9px] text-blue-900 font-bold italic leading-relaxed uppercase tracking-tighter">
               {t('add_item_store.warning_text')}
             </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={`p-4 bg-gray-100 border-t flex justify-end gap-3 px-8 shadow-inner ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-black text-gray-500 hover:bg-gray-200 rounded transition-all uppercase tracking-widest">{t('add_item_store.cancel')}</button>
          <button 
            onClick={handleAssign}
            disabled={loading}
            className="bg-[#1a3353] hover:bg-blue-900 text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50 uppercase tracking-widest"
          >
            {loading ? <Loader2 className="animate-spin" size={14}/> : <Save size={14} />}
            {t('add_item_store.finalize')}
          </button>
        </div>

        <UniversalLookup 
          {...lookup} 
          onClose={() => setLookup({ ...lookup, isOpen: false })}
          onSelect={(row) => {
            const locValue = row.location || row.location_id || row.id;
            const siteValue = row.site_id || row.siteid;
        
            if (lookup.field === 'location') {
                setStoreData(prev => ({ 
                    ...prev, 
                    location: locValue, 
                    siteid: siteValue
                }));
            } else {
                setStoreData(prev => ({ ...prev, [lookup.field]: row.value || row.id }));
            }
            setLookup({ ...lookup, isOpen: false });
          }}
        />
      </div>
    </div>
  );
};

// Helper Component for Units
const UnitLookupField = ({ label, value, onSearch }) => (
    <div className="flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all">
        <label className="text-[8px] font-black text-gray-400 tracking-widest uppercase">{label}</label>
        <div className="flex items-center justify-between py-1 px-1">
            <span className="font-black text-blue-900 text-[11px]">{value || '---'}</span>
            <button onClick={onSearch}><Search size={13} className="text-blue-400 hover:text-blue-600" /></button>
        </div>
    </div>
);

export default AddItemToStoreModal;